// FairPriceCard — one .ex-row in the dashboard table. Markup matches
// the prototype's per-row layout from fair-price-dashboard.jsx:
//   row-mark · ex-fixture · ex-selection · fair→market · ex-edge · sig-conf
//   · recommend chip · ex-actions-cell.
//
// Pure presentation. Reads exactly one FairPriceFixture (already grouped
// by useFairPriceOutputs).

import { ValueEdgeBadge } from "../../components/ValueEdgeBadge.tsx";
import type { FairPriceFixture } from "../../exchange/useFairPriceOutputs.ts";
import { marketEyebrow, renderLegSelection } from "../../exchange/leg-format.ts";

export interface FairPriceCardProps {
  readonly fixture: FairPriceFixture;
  readonly onOpen: () => void;
}

// FPE-CARD (2026-05-29) — local market/selection dictionaries removed.
// Every Powerball/Acca/OWL surface uses renderLegSelection() +
// marketEyebrow() from exchange/leg-format.ts as the single source of
// truth for human-readable picks. FPE was rendering bare "Under" /
// "home_plus_1_5" for the wider markets (AH, halves, OR-combos, etc.).
// Now it speaks the same language as the rest of the platform.

function recBucket(edge: number | null): { cls: "take" | "watch" | "pass"; lab: "TAKE" | "WATCH" | "PASS" } {
  if (edge === null) return { cls: "watch", lab: "WATCH" };
  if (edge >= 5) return { cls: "take", lab: "TAKE" };
  if (edge >= 0) return { cls: "watch", lab: "WATCH" };
  return { cls: "pass", lab: "PASS" };
}

function tierClass(edge: number | null): "tier-1" | "tier-2" | "tier-3" {
  if (edge === null) return "tier-3";
  if (edge >= 5) return "tier-1";
  if (edge >= 0) return "tier-2";
  return "tier-3";
}

function confidenceFromEdge(edge: number | null): number {
  if (edge === null) return 1;
  if (edge >= 10) return 4;
  if (edge >= 5) return 3;
  if (edge >= 0) return 2;
  return 1;
}

function fmtKickoff(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function FairPriceCard({ fixture, onOpen }: FairPriceCardProps): JSX.Element {
  const head = fixture.headline_row;
  const edge = head.edge_pct;
  const edgeCls = edge === null ? "neu" : edge > 0 ? "pos" : edge < 0 ? "neg" : "neu";
  const rec = recBucket(edge);
  const tier = tierClass(edge);
  const conf = confidenceFromEdge(edge);
  const fair = head.fair_odds.toFixed(2);
  const market = head.bookmaker_odds !== null ? head.bookmaker_odds.toFixed(2) : "—";
  // Shared formatter: eyebrow = compact category, headline = the complete
  // user-facing line (line/side/team baked in). Ctx resolves home/away
  // tokens to actual team names.
  const ctx = { home_team: fixture.home_team, away_team: fixture.away_team };
  const headline = renderLegSelection(head.market, head.selection, ctx);
  const eyebrow = marketEyebrow(head.market);

  return (
    <div className={`ex-row ${tier}`} onClick={onOpen}>
      <div className="row-mark" />
      <div className="ex-fixture">
        <div className="teams">
          {fixture.home_team} <span className="vs">vs</span> {fixture.away_team}
        </div>
        <div className="meta">
          <span className="league-tag">{fixture.competition}</span>
          <span className="dot" />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
            {fmtKickoff(fixture.kickoff_at)}
          </span>
        </div>
      </div>
      <div className="ex-selection">
        {/* Eyebrow (small-caps category) over headline (complete user-
            facing line — line/side/team baked in). Same pattern as
            Powerball MM cards. No more bare "Under" / "home_plus_1_5". */}
        <div
          style={{
            fontSize: 9.5,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--text-tertiary)",
            opacity: 0.75,
            marginBottom: 3,
          }}
        >
          {eyebrow}
        </div>
        <div style={{ fontWeight: 600, color: "var(--text)" }}>{headline}</div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "var(--font-mono)",
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--text-primary)" }}>{fair}</span>
        <span style={{ color: "var(--text-tertiary)" }}>→</span>
        <span style={{ color: "var(--text-secondary)" }}>{market}</span>
      </div>
      <div className="ex-edge">
        <div className={`num ${edgeCls}`}>
          {edge === null ? "—" : `${edge > 0 ? "+" : ""}${edge.toFixed(1)}%`}
        </div>
        <div className="lbl">vs market</div>
        <div style={{ marginTop: 4 }}>
          <ValueEdgeBadge
            modelProb={head.fair_prob}
            marketProb={head.bookmaker_prob}
            compact
          />
        </div>
      </div>
      <div className="sig-conf" title={`Confidence ${conf}/4`}>
        {[1, 2, 3, 4].map((p) => (
          <span key={p} className={`pip ${p <= conf ? "on" : ""}`} />
        ))}
      </div>
      <div>
        <span className={`fp-rec-chip ${rec.cls}`}>{rec.lab}</span>
      </div>
      <div className="ex-actions-cell">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          Open
        </button>
      </div>
    </div>
  );
}
